import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { farmAPI, sprayAPI } from '@/lib/api';
import { categoryOf } from '@/data/produce';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SearchSelect from '@/components/SearchSelect';
import {
  AlertTriangle, ArrowLeft, CalendarDays, CheckCircle2, Circle, Info, Leaf, Loader2, Plus, Search, Trash2,
} from 'lucide-react';

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Spray plans. The farmer picks a crop they grow, the product they will
 * spray and the first spray date; the plan comes from the product's
 * label details (every N days, at most N sprays, dose, days before
 * harvest). A product without those details has no plan.
 */
export default function SprayPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [plots, setPlots] = useState([]);
  const [plans, setPlans] = useState([]);

  const formatDate = useCallback((iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  }, [i18n.language]);

  const loadPlans = useCallback(async () => {
    const res = await sprayAPI.plans();
    setPlans(res.data.plans || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [plotsRes] = await Promise.all([farmAPI.getPlots(user.id), loadPlans()]);
        // Crops only: animals, poultry and fish aren't sprayed.
        if (!cancelled) setPlots((plotsRes.data || []).filter((p) => categoryOf(p) === 'crops'));
      } catch (error) {
        console.error('Failed to load spray plans', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user.id, loadPlans]);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-farm-primary" />
        </div>
      );
    }
    if (plots.length === 0 && plans.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Leaf className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-muted-foreground text-center mb-4">{t('spray.noPlots')}</p>
            <Button onClick={() => navigate('/farm')} className="bg-farm-primary hover:bg-farm-primary-dark">
              <Plus className="w-4 h-4 mr-2" /> {t('spray.goToFarm')}
            </Button>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="space-y-6">
        {plots.length > 0 && (
          <NewPlan plots={plots} formatDate={formatDate}
                   onMade={(plan) => setPlans((prev) => [plan, ...prev])} />
        )}
        <div>
          <h2 className="font-heading text-lg font-bold mb-3">{t('spray.myPlans')}</h2>
          {plans.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="spray-no-plans">{t('spray.empty')}</p>
          ) : (
            <div className="space-y-4">
              {plans.map((plan) => (
                <PlanCard key={plan.plan_id || plan.tasks[0].id} plan={plan} formatDate={formatDate}
                          onChanged={() => loadPlans().catch(() => {})} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 animate-fadeIn" data-testid="spray-page">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold">{t('spray.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('spray.subtitle')}</p>
        </div>
      </div>

      <div className="mb-4 flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p>{t('spray.disclaimer')}</p>
      </div>

      {renderBody()}
    </div>
  );
}

function LabelDetails({ spray }) {
  const { t } = useTranslation();
  return (
    <div className="text-sm text-gray-700 space-y-0.5">
      {spray.interval_days && (
        <p>{t('spray.every', { days: spray.interval_days, count: spray.max_applications })}</p>
      )}
      {spray.dose_per_hectare && <p>{t('spray.dosePerHectare', { dose: spray.dose_per_hectare })}</p>}
      {spray.dose_per_knapsack && <p>{t('spray.dosePerKnapsack', { dose: spray.dose_per_knapsack })}</p>}
    </div>
  );
}

function NewPlan({ plots, formatDate, onMade }) {
  const { t } = useTranslation();
  const [plotId, setPlotId] = useState(plots.length === 1 ? plots[0].id : '');
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [product, setProduct] = useState(null);
  const [firstDate, setFirstDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [notAvailable, setNotAvailable] = useState(false);

  useEffect(() => {
    if (product) return undefined;
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await sprayAPI.products(q.trim());
        if (!cancelled) setResults(res.data.items || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q, product]);

  const choose = (item) => {
    setProduct(item);
    setNotAvailable(!item.has_spray_plan);
  };

  const make = async () => {
    setSaving(true);
    try {
      const res = await sprayAPI.makePlan({ plot_id: plotId, product_id: product.id, first_date: firstDate });
      if (!res.data.available) {
        setNotAvailable(true);
        return;
      }
      onMade(res.data.plan);
      toast({ title: t('spray.made') });
      setProduct(null);
      setQ('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast({ title: typeof detail === 'string' ? detail : t('spray.error'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const plotOptions = plots.map((p) => ({
    value: p.id,
    label: p.plot_name && p.plot_name !== p.crop_type ? `${p.crop_type} · ${p.plot_name}` : p.crop_type,
  }));

  return (
    <Card data-testid="spray-plan-form">
      <CardContent className="p-4 space-y-4">
        <p className="font-heading text-lg font-bold">{t('spray.newPlan')}</p>

        <div className="space-y-1">
          <p className="text-sm font-medium">{t('spray.crop')}</p>
          <SearchSelect value={plotId} onChange={setPlotId} options={plotOptions}
                        placeholder={t('spray.chooseCrop')} title={t('spray.crop')} testId="spray-crop" />
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium">{t('spray.product')}</p>
          {product ? (
            <div className="rounded-lg border p-3 space-y-2" data-testid="spray-chosen-product">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold">{product.name}</p>
                  {product.active_ingredients && <p className="text-xs text-gray-500">{product.active_ingredients}</p>}
                </div>
                <Button variant="outline" size="sm" onClick={() => { setProduct(null); setNotAvailable(false); }}
                        data-testid="spray-change-product">
                  {t('spray.change')}
                </Button>
              </div>
              {notAvailable ? (
                <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900"
                     data-testid="spray-not-available">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p>{t('spray.notAvailable')}</p>
                </div>
              ) : (
                product.spray && <LabelDetails spray={product.spray} />
              )}
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('spray.productSearch')}
                       className="h-12 pl-9 bg-white" data-testid="spray-product-search" />
              </div>
              <div className="max-h-64 overflow-y-auto rounded-lg border divide-y bg-white" data-testid="spray-product-results">
                {searching && results.length === 0 ? (
                  <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" /></div>
                ) : results.length === 0 ? (
                  <p className="text-sm text-muted-foreground px-3 py-3">{t('spray.noProducts')}</p>
                ) : results.map((item) => (
                  <button key={item.id} type="button" onClick={() => choose(item)}
                          className="w-full text-left px-3 py-2.5 hover:bg-gray-50"
                          data-testid={`spray-product-${item.id}`}>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-xs text-gray-500">
                      {[item.active_ingredients, item.has_spray_plan ? null : t('spray.noLabelDetails')].filter(Boolean).join(' · ')}
                    </p>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {product && !notAvailable && (
          <>
            <div className="space-y-1">
              <p className="text-sm font-medium">{t('spray.firstDate')}</p>
              <Input type="date" value={firstDate} onChange={(e) => setFirstDate(e.target.value)}
                     className="h-12 bg-white" data-testid="spray-first-date" />
              {firstDate && <p className="text-xs text-gray-500">{formatDate(firstDate)}</p>}
            </div>
            <p className="text-sm text-gray-700" data-testid="spray-only-this-product">
              {t('spray.onlyThisProduct', { product: product.name })}
            </p>
            <Button className="w-full h-12 bg-farm-primary hover:bg-farm-primary-dark text-base"
                    disabled={!plotId || !firstDate || saving} onClick={make} data-testid="spray-make-plan">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CalendarDays className="w-5 h-5 mr-2" />{t('spray.makePlan')}</>}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PlanCard({ plan, formatDate, onChanged }) {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState(plan.tasks);
  const [busy, setBusy] = useState(false);
  const today = todayIso();
  const count = tasks.length;

  useEffect(() => { setTasks(plan.tasks); }, [plan.tasks]);

  const toggle = async (task) => {
    setTasks((prev) => prev.map((x) => (x.id === task.id ? { ...x, done: !task.done } : x)));
    try {
      await sprayAPI.markTask(task.id, !task.done);
    } catch {
      setTasks((prev) => prev.map((x) => (x.id === task.id ? { ...x, done: task.done } : x)));
      toast({ title: t('spray.error'), variant: 'destructive' });
    }
  };

  const remove = async () => {
    if (!window.confirm(t('spray.confirmDelete'))) return;
    setBusy(true);
    try {
      await sprayAPI.deletePlan(plan.plan_id);
      onChanged();
    } catch {
      toast({ title: t('spray.error'), variant: 'destructive' });
      setBusy(false);
    }
  };

  return (
    <Card className="overflow-hidden" data-testid={`spray-plan-${plan.plan_id || tasks[0]?.id}`}>
      <div className="text-white p-4 bg-gradient-to-r from-farm-primary to-farm-success" data-testid="spray-plan-product">
        <p className="text-xs font-semibold uppercase tracking-wide text-white/85">{t('spray.forProduct')}</p>
        <p className="font-bold text-xl leading-tight mt-0.5">{plan.product_name}</p>
        {plan.active_ingredients && <p className="text-white/85 text-sm">{plan.active_ingredients}</p>}
        {plan.crop && <p className="text-white/85 text-sm mt-1">{t('spray.cropLine', { crop: plan.crop })}</p>}
      </div>
      <CardContent className="p-4 space-y-3">
        {/* The dates and doses come from this product's label: another product needs its own plan. */}
        <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900"
             data-testid="spray-product-note">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>{t('spray.changeProductNote', { product: plan.product_name })}</p>
        </div>
        <LabelDetails spray={plan} />
        <ul className="divide-y rounded-lg border">
          {tasks.map((task) => {
            const late = !task.done && task.date < today;
            return (
              <li key={task.id}>
                <button type="button" onClick={() => toggle(task)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                        data-testid={`spray-task-${task.id}`}>
                  {task.done
                    ? <CheckCircle2 className="w-6 h-6 text-farm-primary flex-shrink-0" />
                    : <Circle className={`w-6 h-6 flex-shrink-0 ${late ? 'text-red-500' : 'text-gray-300'}`} />}
                  <div className="min-w-0">
                    <p className={`font-medium ${task.done ? 'line-through text-gray-400' : late ? 'text-red-700' : ''}`}>
                      {formatDate(task.date)}
                    </p>
                    {task.application_no && (
                      <p className="text-xs text-gray-500">{t('spray.sprayN', { n: task.application_no, count })}</p>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        {plan.harvest_from && (
          <p className="text-sm font-semibold text-amber-800" data-testid="spray-harvest-from">
            {t('spray.harvestFrom', { date: formatDate(plan.harvest_from) })}
          </p>
        )}
        {plan.reentry_hours != null && (
          <p className="text-sm text-gray-700">{t('spray.reentry', { hours: plan.reentry_hours })}</p>
        )}
        {plan.plan_id && (
          <Button variant="outline" size="sm" className="text-red-700" disabled={busy} onClick={remove}
                  data-testid="spray-delete-plan">
            <Trash2 className="w-4 h-4 mr-1" /> {t('spray.deletePlan')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
